import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import {
  componentPermission,
  modulePermission,
} from 'src/mindtime/config/subscriptionPermissionRegistry';
import {
  SECURITY_CLIENTS_HREF,
  SECURITY_DASHBOARD_HREF,
  SECURITY_ENDPOINTS_HREF,
  getSecurityDeployAgentHref,
  getSecurityNavItemPath,
} from 'src/mindtime/config/securityNav';

export const SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY = 'unavailable.default';
export const SUBSCRIPTION_UNAVAILABLE_PREVIEW_CREATE_KEY = 'unavailable.previewCreate';

export type SubscriptionUnavailableChrome = {
  title: string;
  subtitle?: string;
  breadcrumb: { links: Array<{ name: string; to: string }> };
};

const navPath = (id: string): string => getSecurityNavItemPath(id) ?? '';

const edrLinks = (
  name: string,
  id: string,
  extra?: { name: string; to: string },
): SubscriptionUnavailableChrome['breadcrumb'] => ({
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name, to: navPath(id) },
    ...(extra ? [extra] : []),
  ],
});

const SUBSCRIPTION_UNAVAILABLE_KEYS_BY_PERMISSION: Record<string, string> = {
  [modulePermission(EDR_SOLUTION_ID, 'security-operations-center')]: 'unavailable.soc',
  [modulePermission(EDR_SOLUTION_ID, 'reseller-dashboard')]: 'unavailable.resellerDashboard',
  [modulePermission(EDR_SOLUTION_ID, 'sub-org-dashboard')]: 'unavailable.subOrgDashboard',
  [modulePermission(EDR_SOLUTION_ID, 'clients')]: 'unavailable.clients',
  [modulePermission(EDR_SOLUTION_ID, 'endpoints')]: 'unavailable.endpoints',
  [componentPermission(EDR_SOLUTION_ID, 'endpoints', 'deployAgent')]: 'unavailable.deployAgent',
  [modulePermission(EDR_SOLUTION_ID, 'configuration-compliance')]:
    'unavailable.configurationCompliance',
  [modulePermission(EDR_SOLUTION_ID, 'file-integrity-monitoring')]:
    'unavailable.fileIntegrityMonitoring',
  [modulePermission(EDR_SOLUTION_ID, 'threat-hunting')]: 'unavailable.threatHunting',
  [modulePermission(EDR_SOLUTION_ID, 'malware-detection')]: 'unavailable.malwareDetection',
  [modulePermission(EDR_SOLUTION_ID, 'automated-response')]: 'unavailable.automatedResponse',
  [modulePermission(EDR_SOLUTION_ID, 'vulnerability-detection')]:
    'unavailable.vulnerabilityDetection',
  [modulePermission(EDR_SOLUTION_ID, 'mitre-attck')]: 'unavailable.mitreAttck',
};

const SUBSCRIPTION_UNAVAILABLE_CHROME: Record<string, SubscriptionUnavailableChrome> = {
  [SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY]: {
    title: 'Security',
    breadcrumb: { links: [{ name: 'Security', to: SECURITY_DASHBOARD_HREF }] },
  },
  'unavailable.soc': {
    title: 'Security Operations Center',
    subtitle: 'Monitor security operations across your environment.',
    breadcrumb: { links: [{ name: 'Security Operations Center', to: SECURITY_DASHBOARD_HREF }] },
  },
  'unavailable.resellerDashboard': {
    title: 'Dashboard',
    subtitle: 'Portfolio health and activity across client organizations.',
    breadcrumb: { links: [{ name: 'Dashboard', to: navPath('reseller-dashboard') }] },
  },
  'unavailable.subOrgDashboard': {
    title: 'Dashboard',
    subtitle: 'Security posture and activity for this organization.',
    breadcrumb: { links: [{ name: 'Dashboard', to: navPath('sub-org-dashboard') }] },
  },
  'unavailable.clients': {
    title: 'Clients',
    subtitle: 'Monitor client organizations in your portfolio.',
    breadcrumb: {
      links: [
        { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
        { name: 'Clients', to: SECURITY_CLIENTS_HREF },
      ],
    },
  },
  'unavailable.clientDetails': {
    title: 'Client details',
    subtitle: 'Organization security overview and endpoints.',
    breadcrumb: edrLinks('Clients', 'clients', { name: 'Details', to: '' }),
  },
  'unavailable.endpoints': {
    title: 'Endpoints',
    subtitle: 'Monitor and manage protected endpoints.',
    breadcrumb: {
      links: [
        { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
        { name: 'Endpoints', to: SECURITY_ENDPOINTS_HREF },
      ],
    },
  },
  'unavailable.endpointDetails': {
    title: 'Endpoint details',
    subtitle: 'Endpoint security posture, detections, and configuration.',
    breadcrumb: {
      links: [
        { name: 'Endpoints', to: SECURITY_ENDPOINTS_HREF },
        { name: 'Details', to: '' },
      ],
    },
  },
  'unavailable.deployAgent': {
    title: 'Deploy New Agent',
    subtitle: 'Generate installation commands for a new endpoint agent.',
    breadcrumb: {
      links: [
        { name: 'Endpoints', to: SECURITY_ENDPOINTS_HREF },
        { name: 'Deploy New Agent', to: getSecurityDeployAgentHref() },
      ],
    },
  },
  'unavailable.configurationCompliance': {
    title: 'Configuration Compliance',
    subtitle: 'Monitor security configuration compliance across all managed endpoints.',
    breadcrumb: edrLinks('Configuration Compliance', 'configuration-compliance'),
  },
  'unavailable.complianceDetail': {
    title: 'Compliance details',
    subtitle: 'Control status and remediation guidance.',
    breadcrumb: edrLinks('Configuration Compliance', 'configuration-compliance', {
      name: 'Details',
      to: '',
    }),
  },
  'unavailable.fileIntegrityMonitoring': {
    title: 'File Monitoring',
    subtitle: 'Track unauthorized file changes, deletions, and modifications.',
    breadcrumb: edrLinks('File Integrity Monitoring', 'file-integrity-monitoring'),
  },
  'unavailable.fileIntegrityDetail': {
    title: 'File integrity details',
    subtitle: 'File change event details.',
    breadcrumb: edrLinks('File Integrity Monitoring', 'file-integrity-monitoring', {
      name: 'Details',
      to: '',
    }),
  },
  'unavailable.threatHunting': {
    title: 'Threat Hunting',
    subtitle:
      'Search, investigate, and analyze endpoint activity to identify potential threats before they become incidents.',
    breadcrumb: edrLinks('Threat Hunting', 'threat-hunting'),
  },
  'unavailable.threatHuntingDetail': {
    title: 'Threat hunting details',
    subtitle: 'Finding details and related activity.',
    breadcrumb: edrLinks('Threat Hunting', 'threat-hunting', { name: 'Details', to: '' }),
  },
  'unavailable.malwareDetection': {
    title: 'Malware Detection',
    subtitle: 'Monitor, investigate, and respond to malware detections across all protected endpoints.',
    breadcrumb: edrLinks('Malware Detection', 'malware-detection'),
  },
  'unavailable.malwareDetail': {
    title: 'Malware details',
    subtitle: 'Detection details and affected endpoints.',
    breadcrumb: edrLinks('Malware Detection', 'malware-detection', { name: 'Details', to: '' }),
  },
  'unavailable.automatedResponse': {
    title: 'Automated Response',
    subtitle: 'Review and monitor automated response actions executed across your environment.',
    breadcrumb: edrLinks('Automated Response', 'automated-response'),
  },
  'unavailable.vulnerabilityDetection': {
    title: 'Vulnerabilities',
    subtitle: 'Identify, prioritize, and remediate security vulnerabilities across protected endpoints.',
    breadcrumb: edrLinks('Vulnerability Detection', 'vulnerability-detection'),
  },
  'unavailable.vulnerabilityDetail': {
    title: 'Vulnerability details',
    subtitle: 'CVE details and affected endpoints.',
    breadcrumb: edrLinks('Vulnerability Detection', 'vulnerability-detection', {
      name: 'Details',
      to: '',
    }),
  },
  'unavailable.mitreAttck': {
    title: 'MITRE ATT&CK',
    subtitle: 'Analyze attacker tactics and techniques detected across your protected environment.',
    breadcrumb: edrLinks('MITRE ATT&CK', 'mitre-attck'),
  },
  'unavailable.mitreDetail': {
    title: 'MITRE ATT&CK details',
    subtitle: 'Technique details and related detections.',
    breadcrumb: edrLinks('MITRE ATT&CK', 'mitre-attck', { name: 'Details', to: '' }),
  },
};

export const getSubscriptionUnavailableMessageKey = (permission: string): string =>
  SUBSCRIPTION_UNAVAILABLE_KEYS_BY_PERMISSION[permission] ?? SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY;

export const getSubscriptionUnavailableChrome = (
  messageKey: string,
): SubscriptionUnavailableChrome =>
  SUBSCRIPTION_UNAVAILABLE_CHROME[messageKey] ??
  SUBSCRIPTION_UNAVAILABLE_CHROME[SUBSCRIPTION_UNAVAILABLE_DEFAULT_KEY];
